/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import isEqual from 'lodash.isequal';
import {translate} from '@ion/core/src/locale/locale';
import CheckBox from '@ion/dls/src/components/checkbox/CheckBox';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import CustomDateRange from './CustomDateRange';
import {
  MultiSuggestionProps,
  SuggestionsType
} from './types';
import {isChecked} from './utils';

/**
 * Renders selection dropdown component that will allow single or multiselection
 * based on dynamic data or predefined data or select custom date range.
 *
 * ```typescript
 * import MultiSelectionSuggestions from '@ion/features/src/search/MultiSelectionSuggestions';
 *
 * <MultiSelectionSuggestions onSelection={onSelection}
 *  classes={classes}
 *  isMultiSelection={menuSelectionType === SuggestionsType.MULTISELECTION}
 *  selectedSuggestions={selectedSuggestions}
 *  listOptions={listOptions}
 *  menuSelectionType={menuSelectionType}/>
 * ```
 */
const MultiSelectionSuggestions = ({
  listOptions,
  onSelection,
  selectedSuggestions,
  classes,
  isMultiSelection,
  menuSelectionType,
  setShowSuggestions,
  hasDateField = false
}: MultiSuggestionProps) => {
  const {isCurrent: isCurrentTheme} = useThemeContext();

  return (
    <>
      {isEqual(menuSelectionType, SuggestionsType.MULTISELECTION) && (
        <MenuItem classes={{root: classes.suggestionItem}}
          value={'all'}
          onClick={onSelection(listOptions)}>
          {isMultiSelection && <CheckBox checked={isChecked(selectedSuggestions, listOptions, false)}
            indeterminate={isChecked(selectedSuggestions, listOptions, true)} />}
          {isCurrentTheme ? (
            <span className={classes.suggestionItemLabel}>
              {translate('common:modularsearch.selectAll')}
            </span>
          ) : translate('common:modularsearch.selectAll')}
        </MenuItem>
      )}
      {listOptions.map((option: string, index: number) => (
        <MenuItem id={`${option}-${index}`}
          classes={{root: classes.suggestionItem}}
          onClick={onSelection(option)}
          key={`suggestion-attribute-${option}-${index}`}>
          {isMultiSelection && <CheckBox checked={selectedSuggestions.includes(option) ||
            isChecked(selectedSuggestions, listOptions, false)} />}
          {isCurrentTheme ? (
            <span className={classes.suggestionItemLabel}>
              {option}
            </span>
          ) : option}
        </MenuItem>
      ))}
      {menuSelectionType === SuggestionsType.DATE && (
        <CustomDateRange onSelection={onSelection}
          menuItemClass={classes.dateMenuItem}
          setShowSuggestions={setShowSuggestions}
          includeContainerStyles={hasDateField}
          menuSubmissionWrapperClass={classes.menuSubmissionWrapperClass}/>
      )}
    </>
  );
};

export default MultiSelectionSuggestions;
